import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useLang } from "@/lib/i18n";
import { NEWS, NEWS_META, NEWS_PATH, formatNewsDate, newsJsonLd } from "@/lib/news";
import Footer from "@/components/berlin/Footer";
import { BrandWordmark } from "@/components/berlin/BrandWordmark";
import { ArrowUpRight, ArrowLeft } from "lucide-react";

const BASE = "https://berlin-cube.de";
const ease = [0.22, 1, 0.36, 1];

const Reveal = ({ children, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.9, ease }}
    className={className}
  >
    {children}
  </motion.div>
);

const setLinkTag = (rel, hreflang, href) => {
  const sel = `link[rel="${rel}"]` + (hreflang ? `[hreflang="${hreflang}"]` : "");
  let el = document.querySelector(sel);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    if (hreflang) el.setAttribute("hreflang", hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

const Badge = ({ type, lang }) => (
  <div className="absolute top-3 left-3 md:top-4 md:left-4 z-10 inline-flex items-center gap-2 text-[10px] tracking-[0.35em] uppercase text-white/85 bg-black/50 backdrop-blur px-3 py-1.5 border border-white/10 font-display">
    <span className="h-1 w-1 rounded-full bg-white/70" />
    {type === "vorfuehrung" ? (lang === "de" ? "Vorführung · Foto" : "Demonstration · Photo") : lang === "de" ? "Heute · Foto" : "Today · Photo"}
  </div>
);

const Post = ({ n, lang }) => {
  const c = n[lang];
  return (
    <article id={n.slug} data-testid={`news-post-${n.slug}`} className="grid lg:grid-cols-12 gap-8 lg:gap-12 py-14 md:py-20 border-t border-white/10">
      <div className="lg:col-span-3">
        <time dateTime={n.date} className="text-[11px] tracking-[0.3em] uppercase text-white/45 font-display" data-testid="news-post-date">
          {formatNewsDate(n.date, lang)}
        </time>
      </div>
      <div className="lg:col-span-9 max-w-3xl">
        <h2 className="font-display font-light tracking-[-0.02em] leading-[1.1] text-white" style={{ fontSize: "clamp(1.6rem, 2.8vw, 2.5rem)" }} data-testid="news-post-title">
          {c.title}
        </h2>
        {n.image && (
          <figure className="mt-8">
            <div className="relative w-full aspect-[16/10] overflow-hidden border border-white/10">
              <img src={n.image.src} alt={n.image.alt[lang]} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
              <Badge type={n.image.type} lang={lang} />
            </div>
          </figure>
        )}
        <div className="mt-7 space-y-5">
          {c.paragraphs.map((p, i) => (
            <p key={i} className="text-white/70 leading-[1.7] text-[15px] md:text-base">{p}</p>
          ))}
        </div>
        <div className="mt-7 flex flex-col gap-2">
          {c.links.map((l) => (
            <Link key={l.to} to={l.to} data-testid={`news-link-${n.slug}-${l.to.replace(/\W+/g, "")}`} className="group inline-flex items-center gap-3 w-fit text-[#F5D77A] hover:text-white transition-colors font-display font-light text-lg tracking-[-0.01em]">
              {l.label}
              <ArrowUpRight className="h-4 w-4 opacity-60 transition-all group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.5} />
            </Link>
          ))}
        </div>
      </div>
    </article>
  );
};

export default function News() {
  const { lang, setLang } = useLang();
  const m = NEWS_META[lang];
  const homeHref = lang === "en" ? "/en/" : "/";

  useEffect(() => { window.scrollTo(0, 0); }, []);

  useEffect(() => {
    document.title = m.title;
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute("content", m.description);
    setLinkTag("canonical", null, `${BASE}${NEWS_PATH[lang]}`);
    setLinkTag("alternate", "de", `${BASE}${NEWS_PATH.de}`);
    setLinkTag("alternate", "en", `${BASE}${NEWS_PATH.en}`);
    setLinkTag("alternate", "x-default", `${BASE}${NEWS_PATH.de}`);
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.id = "news-jsonld";
    s.textContent = JSON.stringify(newsJsonLd(lang, BASE));
    document.head.appendChild(s);
    return () => {
      s.remove();
      setLinkTag("alternate", "de", `${BASE}/`);
      setLinkTag("alternate", "en", `${BASE}/en/`);
      setLinkTag("alternate", "x-default", `${BASE}/`);
    };
  }, [lang, m]);

  return (
    <div className="bg-[#0F1114] text-white min-h-screen" data-testid="news-page">
      <header className="fixed top-0 inset-x-0 z-50 bg-[#0F1114]/85 backdrop-blur border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-14 h-16 flex items-center justify-between">
          <Link to={homeHref} translate="no" className="notranslate inline-flex items-center gap-2 font-display font-semibold tracking-[0.24em] text-sm text-white" data-testid="news-brand-link">
            <BrandWordmark lineClass="bg-white/80" />
          </Link>
          <div className="flex items-center gap-4">
            <div className="flex items-center border border-white/15 text-[11px] tracking-[0.22em]" data-testid="news-lang-toggle">
              <button onClick={() => setLang("de")} data-testid="lang-de" className={`px-3 py-1.5 transition-colors ${lang === "de" ? "bg-white text-[#0F1114]" : "text-white/60 hover:text-white"}`}>DE</button>
              <button onClick={() => setLang("en")} data-testid="lang-en" className={`px-3 py-1.5 transition-colors ${lang === "en" ? "bg-white text-[#0F1114]" : "text-white/60 hover:text-white"}`}>EN</button>
            </div>
            <a href={`${homeHref}#contact`} data-testid="news-nav-cta" className="hidden md:inline-flex px-5 py-2 bg-[#F5D77A] text-[#1F2328] font-medium text-sm tracking-wide hover:bg-white transition-colors">
              {lang === "de" ? "Gespräch anfragen" : "Request meeting"}
            </a>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 md:px-14 pt-32 md:pt-40 pb-24 md:pb-32">
        <Reveal className="max-w-4xl">
          <Link to={homeHref} data-testid="news-back-home" className="inline-flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase text-white/45 hover:text-white transition-colors font-display">
            <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.5} />
            {lang === "de" ? "Startseite" : "Home"}
          </Link>
          <div className="mt-10 flex items-center gap-3 text-[11px] tracking-[0.35em] uppercase text-white/45 mb-8 font-display">
            <span className="h-px w-8 bg-white/40" />
            {m.kicker}
          </div>
          <h1 className="font-display font-light tracking-[-0.03em] leading-[1.02] text-white" style={{ fontSize: "clamp(2.4rem, 5.5vw, 4.8rem)" }} data-testid="news-h1">
            {m.h1}
          </h1>
          <p className="mt-8 text-white/70 font-display font-light tracking-[-0.005em] leading-[1.4] max-w-3xl" style={{ fontSize: "clamp(1.05rem, 1.5vw, 1.45rem)" }}>
            {m.intro}
          </p>
        </Reveal>

        <div className="mt-16 md:mt-24 border-b border-white/10" data-testid="news-list">
          {NEWS.map((n) => <Post key={n.slug} n={n} lang={lang} />)}
        </div>
      </main>

      <Footer />
    </div>
  );
}
